import { useCallback, useEffect, useMemo, useState } from 'react';
import CurriculumSectionsPanel from './CurriculumSectionsPanel';
import CurriculumEditorPanel from './CurriculumEditorPanel';
import { makeEmptySection, normalizeCurriculumOutline } from './curriculumUtils';
import './CurriculumBuilder.css';

/**
 * Curriculum outline builder (sections + topics).
 * @param {string} courseTitle - Mirrors main course name field.
 * @param {Array<{id:string,title:string,color:string,topics:string[]}>} value
 * @param {(next: typeof value) => void} onChange
 */
export default function CurriculumBuilder({ courseTitle = '', value, onChange }) {
  const sections = useMemo(() => normalizeCurriculumOutline(value), [value]);

  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!sections.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !sections.some((s) => s.id === selectedId)) {
      setSelectedId(sections[0].id);
    }
  }, [sections, selectedId]);

  const pushSections = useCallback(
    (next) => {
      onChange(normalizeCurriculumOutline(next));
    },
    [onChange]
  );

  const handleAddSection = () => {
    const next = [...sections, makeEmptySection(sections.length)];
    pushSections(next);
    setSelectedId(next[next.length - 1].id);
  };

  const handleDeleteSection = (id) => {
    const idx = sections.findIndex((s) => s.id === id);
    const next = sections.filter((s) => s.id !== id);
    pushSections(next);
    if (selectedId === id) {
      if (next.length === 0) setSelectedId(null);
      else setSelectedId(next[Math.max(0, idx - 1)]?.id ?? next[0].id);
    }
  };

  const handleUpdateTitle = (title) => {
    if (!selectedId) return;
    pushSections(sections.map((s) => (s.id === selectedId ? { ...s, title } : s)));
  };

  const handleUpdateTopic = (topicIndex, text) => {
    if (!selectedId) return;
    pushSections(
      sections.map((s) => {
        if (s.id !== selectedId) return s;
        const topics = [...s.topics];
        topics[topicIndex] = text;
        return { ...s, topics };
      })
    );
  };

  const handleDeleteTopic = (topicIndex) => {
    if (!selectedId) return;
    pushSections(
      sections.map((s) => {
        if (s.id !== selectedId) return s;
        return { ...s, topics: s.topics.filter((_, i) => i !== topicIndex) };
      })
    );
  };

  const handleAddTopic = (text) => {
    if (!selectedId) return;
    pushSections(
      sections.map((s) => (s.id === selectedId ? { ...s, topics: [...s.topics, text] } : s))
    );
  };

  const selected = sections.find((s) => s.id === selectedId) ?? null;
  const sectionNumber = selected ? sections.findIndex((s) => s.id === selected.id) + 1 : 0;

  const logCurriculum = () => {
    const payload = {
      courseTitle: courseTitle?.trim() || '(untitled course)',
      sections: normalizeCurriculumOutline(sections),
    };
    console.log('Curriculum outline (preview)', payload);
  };

  return (
    <div className="cb-root">
      <header className="cb-header">
        <div className="cb-header__titles">
          <h2>Course curriculum</h2>
          <p>Build sections and topics — structured outline, no file upload.</p>
          <p className="cb-course-line">
            <strong>Linked course name:</strong>{' '}
            {courseTitle?.trim() ? (
              <span>{courseTitle.trim()}</span>
            ) : (
              <em>Enter the course name in the field above</em>
            )}
          </p>
        </div>
        <button type="button" onClick={logCurriculum} className="cb-btn-log">
          Log curriculum (console)
        </button>
      </header>

      <div className="cb-layout">
        <CurriculumSectionsPanel
          sections={sections}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onAddSection={handleAddSection}
          onDeleteSection={handleDeleteSection}
        />
        <CurriculumEditorPanel
          section={selected}
          sectionNumber={sectionNumber}
          onUpdateTitle={handleUpdateTitle}
          onUpdateTopic={handleUpdateTopic}
          onDeleteTopic={handleDeleteTopic}
          onAddTopic={handleAddTopic}
        />
      </div>
    </div>
  );
}
